/**
 * ตรรกะล้วนของหน้า "แผนการผลิต" (`WO-P2-M5-006`, D-127/D-110 ข้อ 8) แยกออกจาก JSX
 * เพื่อให้ทดสอบกฎได้ตรง ๆ — แนวเดียวกับ `lib/mrp/mrpView.ts`
 *
 * สี่ข้อที่ผิดแล้วแพงที่สุดของหน้านี้:
 *
 * 1. **"คงเหลือที่ต้องผลิต" คำนวณที่นี่ด้วยเลขจำนวนเต็มสเกล 4 เท่านั้น** (D-127 ข้อ 3)
 *    — `planned_qty`/`actual_good_qty` เป็น `NUMERIC(18, 4)` ที่กว้างกว่าที่ IEEE-754
 *    double เก็บได้ครบ `Number(...)` จะเสียค่าแบบเงียบ ๆ ทุกฟังก์ชันที่นี่จึงเดินบน
 *    `BigInt` สเกล 10,000 แทน ไม่ผ่าน `Number`/`parseFloat` ที่ไหนเลย
 * 2. **ค่านี้ไม่เคยเดินทางไปเซิร์ฟเวอร์** — เป็นเลขที่หน้าจอคำนวณเพื่อแสดงและใช้ prefill
 *    ลิงก์สร้างใบสั่งผลิตเท่านั้น
 * 3. **ค่าเริ่มต้นของ dropdown เลือกแผน** (`pickDefaultMpsVersion`) ไม่เลือก `CANCELLED`
 *    ให้เองเด็ดขาด (D-127 ข้อ 2) ส่วน `DRAFT` เป็น fallback ท้ายสุดเมื่อไม่มีสามสถานะ
 *    ที่สูงกว่าเลย (D-131 ข้อ 4)
 * 4. **`attainment_pct` ที่เป็น `null` ต้องแสดงเป็น "—" ไม่ใช่ "0%"** (D-110 ข้อ 7)
 *    — ทั้งสองมีความหมายต่างกัน: `null` คือ "ไม่มีเปอร์เซ็นต์ที่ถูกต้อง" ส่วน `"0.00"`
 *    คือ "แผนสั่งจริงแต่ไม่ได้ของเลย"
 */

import {
  APPROVED_MPS_STATUS,
  CANCELLED_MPS_STATUS,
  DRAFT_MPS_STATUS,
  FROZEN_MPS_STATUS,
  RELEASED_MPS_STATUS,
  type MpsAttainmentItem,
  type MpsLineDetail,
  type MpsVersionCreateBody,
  type MpsVersionDetail,
  type MpsVersionSummary,
  type MpsWorkCenterOption,
} from './types';
import type { Product } from '../products/types';
import { isProductSearchQueryEligible, productOptionLabel } from '../sales/salesView';

/* ------------------------------------------------------------- สถานะ ----- */

/**
 * ชื่อที่อ่านออกของสถานะแผน — คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น
 *
 * `DRAFT` แสดงเป็น **"วางแผนแบบ Manual"** ไม่ใช่ "ร่าง (DRAFT)" (D-131 ข้อ 3) — ค่า
 * `status` ที่ส่ง/รับจากเซิร์ฟเวอร์ยังเป็น `DRAFT` เดิมทุกประการ เปลี่ยนแค่ป้ายที่
 * แสดง ที่นี่ที่เดียวให้ทุกหน้าเห็นชื่อตรงกัน
 */
export function mpsVersionStatusLabel(status: string): string {
  switch (status) {
    case DRAFT_MPS_STATUS:
      return 'วางแผนแบบ Manual';
    case APPROVED_MPS_STATUS:
      return 'อนุมัติแล้ว (APPROVED)';
    case FROZEN_MPS_STATUS:
      return 'ล็อกแล้ว (FROZEN)';
    case RELEASED_MPS_STATUS:
      return 'ปล่อยใช้งานแล้ว (RELEASED)';
    case CANCELLED_MPS_STATUS:
      return 'ยกเลิกแล้ว (CANCELLED)';
    default:
      return status;
  }
}

/** ข้อความบนตัวเลือกใน dropdown — `"version_label — โรงงาน — สถานะ"` (D-127 ข้อ 2) */
export function mpsVersionOptionLabel(version: MpsVersionSummary): string {
  return `${version.version_label} — ${version.plant_code} — ${mpsVersionStatusLabel(version.status)}`;
}

/** ข้อความบนตัวเลือกของ dropdown "Line ผลิต" — `"work_center_code — name"` (D-128 ข้อ 4) */
export function workCenterOptionLabel(option: MpsWorkCenterOption): string {
  return `${option.work_center_code} — ${option.name}`;
}

/**
 * ลำดับสถานะที่ `pickDefaultMpsVersion` ไล่หา — **RELEASED ก่อน แล้ว FROZEN แล้ว
 * APPROVED** ตามลำดับที่ D-127 ข้อ 2 ระบุ `DRAFT`/`CANCELLED` ไม่อยู่ในรายการนี้เลย
 */
const DEFAULT_SELECTION_PRIORITY: readonly string[] = [
  RELEASED_MPS_STATUS,
  FROZEN_MPS_STATUS,
  APPROVED_MPS_STATUS,
];

/**
 * แผนเริ่มต้นของ dropdown — **ล่าสุด** (สร้างล่าสุด) ของสถานะแรกในลำดับความสำคัญที่
 * มีอยู่จริง ไม่มีสามสถานะนั้นเลยแต่มี `DRAFT` → ใช้ `DRAFT` ล่าสุด (D-131 ข้อ 4 — ทุก
 * แผนตอนนี้เกิดจากการวางแผนแบบ Manual ผู้ใช้ต้องเห็นแผนที่เพิ่งสร้าง) คืน `null`
 * เมื่อไม่มีแผนในสี่สถานะนี้เลย (รวมถึงตอนที่ไม่มีแผนเลย) — `CANCELLED` ไม่ถูกเลือกเอง
 *
 * "ล่าสุด" เทียบด้วย `created_at` ซึ่งเป็นสตริงความยาวคงที่จากเซิร์ฟเวอร์ (`TimestampOut`)
 * — เทียบด้วย `>` ตรง ๆ ได้โดยไม่ต้องผ่าน `Date` เพราะรูปแบบเรียงตามเวลาอยู่แล้ว
 */
export function pickDefaultMpsVersion(
  items: readonly MpsVersionSummary[],
): MpsVersionSummary | null {
  for (const status of DEFAULT_SELECTION_PRIORITY) {
    const candidates = items.filter((item) => item.status === status);
    if (candidates.length === 0) {
      continue;
    }
    return candidates.reduce((latest, item) =>
      item.created_at > latest.created_at ? item : latest,
    );
  }
  const draftCandidates = items.filter((item) => item.status === DRAFT_MPS_STATUS);
  if (draftCandidates.length === 0) {
    return null;
  }
  return draftCandidates.reduce((latest, item) => (item.created_at > latest.created_at ? item : latest));
}

/* ------------------------------------------------- เลขจำนวนเต็มสเกล 4 ----- */

const AMOUNT_SCALE = 4;

/**
 * แปลงสตริง `NUMERIC(18, 4)` เป็น `BigInt` ที่คูณด้วย 10,000 แล้ว — **ไม่ผ่าน
 * `Number`/`parseFloat` เลย** เพื่อไม่ให้ความกว้างของ `NUMERIC(18, 4)` เสียค่าเมื่อ
 * ชนขอบของ IEEE-754 double
 */
function parseScaledAmount(value: string): bigint {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [wholePartRaw, fractionPartRaw = ''] = unsigned.split('.');
  const wholePart = wholePartRaw === '' ? '0' : wholePartRaw;
  const fractionPart = (fractionPartRaw + '0'.repeat(AMOUNT_SCALE)).slice(0, AMOUNT_SCALE);
  const magnitude = BigInt(`${wholePart}${fractionPart}`);
  return negative ? -magnitude : magnitude;
}

/** แปลงกลับเป็นสตริงที่สเกล 4 ตำแหน่งเสมอ — รูปเดียวกับที่เซิร์ฟเวอร์ส่งมา */
function formatScaledAmount(scaled: bigint): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const digits = magnitude.toString().padStart(AMOUNT_SCALE + 1, '0');
  const wholePart = digits.slice(0, digits.length - AMOUNT_SCALE);
  const fractionPart = digits.slice(digits.length - AMOUNT_SCALE);
  return `${negative ? '-' : ''}${wholePart}.${fractionPart}`;
}

/**
 * คงเหลือที่ต้องผลิต = `max(0, planned_qty − actual_good_qty)` (D-127 ข้อ 3) —
 * คำนวณด้วยเลขจำนวนเต็มสเกล 4 ล้วน ผลิตเกินแผนตอบ `"0.0000"` เสมอ ไม่ใช่ค่าติดลบ
 */
export function remainingToProduceQty(plannedQty: string, actualGoodQty: string): string {
  const remaining = parseScaledAmount(plannedQty) - parseScaledAmount(actualGoodQty);
  return formatScaledAmount(remaining > 0n ? remaining : 0n);
}

/** คงเหลือนี้มากกว่าศูนย์จริงหรือไม่ — ด่านเดียวที่ตัดสินว่าปุ่ม "สร้างใบสั่งผลิต" ขึ้น */
export function hasRemainingToProduce(remainingQty: string): boolean {
  return parseScaledAmount(remainingQty) > 0n;
}

/** เทียบสองจำนวนที่สเกล 4 — ใช้กับการเรียงคอลัมน์ตัวเลข ไม่ผ่าน `Number` เช่นกัน */
function compareScaledAmounts(a: string, b: string): number {
  const left = parseScaledAmount(a);
  const right = parseScaledAmount(b);
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

/** เทียบ `attainment_pct` — `null` (ไม่มีเปอร์เซ็นต์) เรียงอยู่ก่อนค่าที่มีเสมอ ไม่ว่า
 * ทิศไหน เพราะมันไม่ใช่ตัวเลขที่เทียบมากน้อยกับอะไรได้ */
function compareAttainmentPct(a: string | null, b: string | null): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return -1;
  }
  if (b === null) {
    return 1;
  }
  return compareScaledAmounts(a, b);
}

/** ข้อความของ `attainment_pct` — `null` คือ "—" ไม่ใช่ "0%" (D-110 ข้อ 7) */
export function attainmentPctLabel(value: string | null): string {
  return value === null ? '—' : `${value}%`;
}

/* ------------------------------------------------- วันที่/เวลาไทยของ timestamp (D-144) ----- */

const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

/** วันที่ (Asia/Bangkok) ของ timestamp ที่ส่งมา — เลขวันจาก UTC+7 ล้วน ๆ ไม่พึ่ง
 * `Intl`/timezone ของเครื่อง (D-144); ใช้กับทั้ง `created_at` และ `released_at`
 * (D-161); `timestamp` ที่อ่านไม่ออก → "—" */
export function planningDateOf(timestamp: string): string {
  const epochMs = Date.parse(timestamp);
  if (Number.isNaN(epochMs)) {
    return '—';
  }
  const bangkok = new Date(epochMs + BANGKOK_OFFSET_MS);
  const year = bangkok.getUTCFullYear();
  const month = String(bangkok.getUTCMonth() + 1).padStart(2, '0');
  const day = String(bangkok.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** วันที่และเวลา (Asia/Bangkok) ของ timestamp ที่ส่งมา รูป `YYYY-MM-DD HH:MM` — คำนวณจาก UTC+7 เช่นเดียวกัน */
export function planningDateTimeOf(timestamp: string): string {
  const date = planningDateOf(timestamp);
  if (date === '—') {
    return date;
  }
  const bangkok = new Date(Date.parse(timestamp) + BANGKOK_OFFSET_MS);
  const hh = String(bangkok.getUTCHours()).padStart(2, '0');
  const mm = String(bangkok.getUTCMinutes()).padStart(2, '0');
  return `${date} ${hh}:${mm}`;
}

/* --------------------------------------------------- แถวที่หน้าจอแสดง ----- */

/** หนึ่งแถวของตาราง — item ของเซิร์ฟเวอร์ + คงเหลือที่คำนวณแล้วแนบมาด้วย */
export interface ProductionPlanRow {
  readonly item: MpsAttainmentItem;
  readonly remainingQty: string;
}

export function toProductionPlanRow(item: MpsAttainmentItem): ProductionPlanRow {
  return { item, remainingQty: remainingToProduceQty(item.planned_qty, item.actual_good_qty) };
}

export type ProductionPlanSortColumn =
  | 'product_code'
  | 'product_name'
  | 'model'
  | 'bucket_date'
  | 'planning_date'
  | 'planned_qty'
  | 'actual_good_qty'
  | 'remaining_qty'
  | 'attainment_pct'
  | 'plant_code';

/** ค่าเริ่มต้น — ตรงกับลำดับเดิมที่เซิร์ฟเวอร์ส่งมา (`product_code`, `bucket_date`) */
export const DEFAULT_SORT_COLUMN: ProductionPlanSortColumn = 'product_code';

function compareText(a: string, b: string): number {
  return a.localeCompare(b, 'th', { numeric: true });
}

/** Model ว่าง (`null`) เรียงอยู่ท้ายสุดเสมอ**ทั้งสองทิศ** (D-133 ข้อ 3/WO-018 acceptance) — รับ `direction` ตรงเพราะพิน
 * ตำแหน่งแบบนี้ทำด้วยการ reverse ทั้งอาเรย์แบบคอลัมน์อื่นไม่ได้ */
function compareModelPinningEmptyLast(
  a: string | null,
  b: string | null,
  direction: 'asc' | 'desc',
): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  const cmp = compareText(a, b);
  return direction === 'asc' ? cmp : -cmp;
}

function compareRows(a: ProductionPlanRow, b: ProductionPlanRow, column: ProductionPlanSortColumn): number {
  switch (column) {
    case 'product_code':
      return compareText(a.item.product_code, b.item.product_code);
    case 'product_name':
      return compareText(a.item.product_name, b.item.product_name);
    case 'model':
      return compareModelPinningEmptyLast(a.item.model, b.item.model, 'asc');
    case 'bucket_date':
      return compareText(a.item.bucket_date, b.item.bucket_date);
    case 'planning_date':
      // ตารางนี้ผูกกับแผนเดียวเสมอ (D-144) — released_at (D-161) ของทุกแถวจึงเป็นค่า
      // เดียวกัน ไม่มีอะไรให้เทียบต่างจากกัน ดู sortProductionPlanRows ด้านล่างที่เทียบ
      // ผ่าน compareModelPinningEmptyLast จริงแทนการ skip
      return 0;
    case 'plant_code':
      return compareText(a.item.plant_code, b.item.plant_code);
    case 'planned_qty':
      return compareScaledAmounts(a.item.planned_qty, b.item.planned_qty);
    case 'actual_good_qty':
      return compareScaledAmounts(a.item.actual_good_qty, b.item.actual_good_qty);
    case 'remaining_qty':
      return compareScaledAmounts(a.remainingQty, b.remainingQty);
    case 'attainment_pct':
      return compareAttainmentPct(a.item.attainment_pct, b.item.attainment_pct);
  }
}

/**
 * เรียงแถว**ของหน้าที่โหลดมาแล้วเท่านั้น** — ไม่ใช่ทั้งชุดข้อมูล (D-123: การเรียงฝั่ง
 * เบราว์เซอร์ถูกต้องก็ต่อเมื่อโหลดครบทุกแถว) ตารางนี้แบ่งหน้าด้วย cursor จากเซิร์ฟเวอร์
 * 20 แถวต่อครั้ง การเรียงจึงเป็นการจัดลำดับใหม่ของ 20 แถวที่เห็นอยู่ ไม่ใช่คำสัญญาว่า
 * ทั้งชุดถูกเรียงแล้ว — สลับหน้าแล้วคอลัมน์ที่เรียงไว้ยังจำได้เหมือนเดิม
 *
 * **`model`/`planning_date` ไม่ผ่าน reverse ทั่วไปด้านล่าง** — `model` ต้องพิน
 * ค่าว่างไว้ท้ายสุดไม่ว่าทิศไหน (D-133 ข้อ 3) ส่วน `planning_date` ก็เทียบด้วย
 * comparator ตัวเดียวกัน (pin "—" ท้ายสุดทั้งสองทิศ, D-161) แต่ `releasedAt` เป็น
 * ค่าเดียวของทั้งตาราง (ตารางนี้ผูกกับแผนเดียว) จึงเทียบแล้วเท่ากันเสมอทุกคู่แถว
 */
export function sortProductionPlanRows(
  rows: readonly ProductionPlanRow[],
  column: ProductionPlanSortColumn,
  direction: 'asc' | 'desc',
  releasedAt: string | null = null,
): readonly ProductionPlanRow[] {
  if (column === 'model') {
    return [...rows].sort((a, b) => compareModelPinningEmptyLast(a.item.model, b.item.model, direction));
  }
  if (column === 'planning_date') {
    return [...rows].sort(() => compareModelPinningEmptyLast(releasedAt, releasedAt, direction));
  }
  const sorted = [...rows].sort((a, b) => compareRows(a, b, column));
  return direction === 'asc' ? sorted : sorted.reverse();
}

/* --------------------------------------------------------------- ลิงก์ ---- */

/**
 * search ของลิงก์ "สร้างใบสั่งผลิต" — `/production/orders?view=create&...` (D-127
 * ข้อ 4) **เป็นลิงก์ล้วน ไม่ยิง API จากหน้านี้เลย** หน้าสร้างใบสั่งผลิตรับค่าพวกนี้
 * ใน `WO-P2-M5-007` ที่ทำคู่กัน
 */
export function productionOrderCreateSearch(params: {
  readonly plantCode: string;
  readonly productCode: string;
  readonly plannedQuantity: string;
  readonly bucketDate: string;
}): string {
  const query = new URLSearchParams();
  query.set('view', 'create');
  query.set('plant_code', params.plantCode);
  query.set('product_code', params.productCode);
  query.set('planned_quantity', params.plannedQuantity);
  query.set('planned_start_date', params.bucketDate);
  query.set('planned_end_date', params.bucketDate);
  return `?${query.toString()}`;
}

/** search ของลิงก์ "ดูสถานะ" — `/production/orders?product_code=&plant_code=` (D-127
 * ข้อ 4) เป็นลิงก์ล้วนเช่นกัน ไม่มีปุ่มไหนในหน้านี้ยิง API ของใบสั่งผลิตเลย */
export function productionOrderStatusSearch(params: {
  readonly productCode: string;
  readonly plantCode: string;
}): string {
  const query = new URLSearchParams();
  query.set('product_code', params.productCode);
  query.set('plant_code', params.plantCode);
  return `?${query.toString()}`;
}

/* ------------------------------------- หน้ารายการ "วางแผนผลิตแบบ Manual" (WO-P2-M5-010, D-129) --- */

/** ฝาแฝดของ `StatusTone` ใน `desktop/StatusBadge.tsx` — restated แทนการ import ข้าม layer */
export type MpsVersionStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'planned';

/** สีของป้ายสถานะแผนบนหน้ารายการ — ไล่ตามลำดับ lifecycle (D-096) */
export function mpsVersionStatusTone(status: string): MpsVersionStatusTone {
  switch (status) {
    case DRAFT_MPS_STATUS:
      return 'planned';
    case APPROVED_MPS_STATUS:
      return 'info';
    case FROZEN_MPS_STATUS:
      return 'warning';
    case RELEASED_MPS_STATUS:
      return 'success';
    case CANCELLED_MPS_STATUS:
      return 'danger';
    default:
      return 'neutral';
  }
}

/* ------------------------------------- หน้าสร้างแผนใหม่ด้วยมือ (WO-P2-M5-010, D-129) --- */

/**
 * หนึ่งบรรทัดที่กำลังกรอกบนหน้าสร้าง — ยังเป็นสตริงดิบทั้งหมด ยังไม่ผ่านการตรวจ
 * `key` คือ id ภายในของแถว (เพิ่ม/ลบแถวได้) ไม่ใช่ฟิลด์ที่ส่งไปเซิร์ฟเวอร์
 *
 * **`productName`/`model` (D-132 ข้อ 2, ลอก `SalesOrderLineDraft` ของ D-117) เป็น
 * ค่าแสดงผลอย่างเดียว ไม่ถูกส่งไปกับ `MpsLineCreateInput` เลย** — สตริงว่างแปลว่า
 * "ยังไม่ได้ resolve" แก้ `productCode` แล้วต้องกลับมาว่างทันที (ดู
 * `withClearedManualPlanLineProductCode`/`withResolvedManualPlanLineProduct`)
 */
export interface ManualPlanLineDraft {
  readonly key: number;
  readonly productCode: string;
  readonly productName: string;
  readonly model: string;
  readonly bucketDate: string;
  readonly plannedQty: string;
}

/** ไม่มี `versionLabel` อีกแล้ว (D-132 ข้อ 1) — เลขแผนออกโดยเซิร์ฟเวอร์เสมอ */
export interface ManualPlanHeaderDraft {
  readonly plantCode: string;
  readonly horizonStart: string;
  readonly horizonEnd: string;
  readonly note: string;
}

/** แถวที่ยังไม่ได้กรอกอะไรเลยสักช่อง — ข้ามตอนตรวจและตอนประกอบ body (D-129 ข้อ 4) */
export function manualPlanLineIsEmpty(line: ManualPlanLineDraft): boolean {
  return (
    line.productCode.trim() === '' && line.bucketDate.trim() === '' && line.plannedQty.trim() === ''
  );
}

/**
 * จำนวนที่ผ่าน `QuantityIn` ได้ — สตริงล้วน ไม่ติดลบ ทศนิยมไม่เกิน 4 ตำแหน่ง (D-129
 * ข้อ 4) **ไม่ผ่าน `Number`/`parseFloat` เลย** เหตุผลเดียวกับ `parseScaledAmount`
 * ข้างบน ศูนย์ผ่านได้ (schema เซิร์ฟเวอร์เช็ก `>= 0` ไม่ใช่ `> 0`)
 */
export function isNonNegativePlanQty(value: string): boolean {
  return /^\d{1,14}(\.\d{1,4})?$/.test(value.trim());
}

export type ManualPlanLineErrorCode =
  | 'PRODUCT_CODE_REQUIRED'
  | 'DATE_REQUIRED'
  | 'DATE_OUTSIDE_HORIZON'
  | 'QTY_INVALID'
  | 'PRODUCT_CODE_NOT_FOUND'
  | 'DUPLICATE_PRODUCT_DATE';

/** ข้อความของแต่ละ error ที่บรรทัด — แสดงข้างแถวที่ผิด (D-129 ข้อ 4) */
export function manualPlanLineErrorLabel(code: ManualPlanLineErrorCode): string {
  switch (code) {
    case 'PRODUCT_CODE_REQUIRED':
      return 'กรอกรหัสสินค้า';
    case 'DATE_REQUIRED':
      return 'กรอกวันที่';
    case 'DATE_OUTSIDE_HORIZON':
      return 'วันที่อยู่นอกช่วงวางแผน';
    case 'QTY_INVALID':
      return 'จำนวนต้องไม่ติดลบ และทศนิยมไม่เกิน 4 ตำแหน่ง';
    case 'PRODUCT_CODE_NOT_FOUND':
      return 'ไม่พบรหัสสินค้านี้';
    case 'DUPLICATE_PRODUCT_DATE':
      return 'รหัสสินค้าและวันที่นี้ซ้ำกับอีกบรรทัด';
  }
}

export interface ManualPlanHeaderErrors {
  readonly plantCode: boolean;
  readonly horizon: boolean;
}

/**
 * ผลตรวจทั้งแบบ — header + แต่ละบรรทัด (คีย์ด้วย `key` ของแถว ไม่ใช่ index เพราะแถว
 * ลบกลางตารางได้) `filledLineCount` คือจำนวนแถวที่กรอกอย่างน้อยหนึ่งช่อง (D-129 ข้อ 4
 * ต้องมีอย่างน้อยหนึ่งแถวที่กรอก**ครบ** — ดู `isManualPlanDraftValid`)
 */
export interface ManualPlanValidation {
  readonly headerErrors: ManualPlanHeaderErrors;
  readonly lineErrors: ReadonlyMap<number, ManualPlanLineErrorCode>;
  readonly filledLineCount: number;
}

/**
 * ตรวจทั้งแบบก่อนส่ง — **ไม่ยิง request ใด ๆ** (D-129 ข้อ 4, acceptance "validate
 * ก่อนส่ง") ไล่ตรวจทีละแถวตามลำดับที่กรอก: รหัสสินค้าก่อน แล้ววันที่ (ต้องอยู่ใน
 * ช่วงวางแผน) แล้วจำนวน แล้วรหัสต้องมีอยู่ในทะเบียนสินค้า แล้วจึงเช็กรหัส+วันที่ซ้ำ
 * — แถวที่ซ้ำกันถูก mark ทั้งสองแถว
 *
 * **`knownProductCodes` เป็น `null` เมื่อไม่รู้ว่าโหลดทะเบียนสินค้าสำเร็จหรือยัง**
 * (D-132 ข้อ 2, invariant "ทน") — ข้ามการตรวจรหัสไปเลย ปล่อยให้เซิร์ฟเวอร์ตัดสินตอน
 * ส่งจริง ไม่ใช่บล็อกฟอร์มเพราะโหลดรายการสินค้าไม่สำเร็จ
 */
export function validateManualPlanDraft(
  header: ManualPlanHeaderDraft,
  lines: readonly ManualPlanLineDraft[],
  knownProductCodes: ReadonlySet<string> | null = null,
): ManualPlanValidation {
  const horizonStart = header.horizonStart.trim();
  const horizonEnd = header.horizonEnd.trim();
  const headerErrors: ManualPlanHeaderErrors = {
    plantCode: header.plantCode.trim() === '',
    horizon: horizonStart === '' || horizonEnd === '',
  };

  const lineErrors = new Map<number, ManualPlanLineErrorCode>();
  const firstSeenKeyByDedupe = new Map<string, number>();
  let filledLineCount = 0;

  for (const line of lines) {
    if (manualPlanLineIsEmpty(line)) {
      continue;
    }
    filledLineCount += 1;

    const productCode = line.productCode.trim();
    const bucketDate = line.bucketDate.trim();
    const plannedQty = line.plannedQty.trim();

    if (productCode === '') {
      lineErrors.set(line.key, 'PRODUCT_CODE_REQUIRED');
      continue;
    }
    if (bucketDate === '') {
      lineErrors.set(line.key, 'DATE_REQUIRED');
      continue;
    }
    if (!headerErrors.horizon && (bucketDate < horizonStart || bucketDate > horizonEnd)) {
      lineErrors.set(line.key, 'DATE_OUTSIDE_HORIZON');
      continue;
    }
    if (!isNonNegativePlanQty(plannedQty)) {
      lineErrors.set(line.key, 'QTY_INVALID');
      continue;
    }
    if (knownProductCodes !== null && !knownProductCodes.has(productCode.toUpperCase())) {
      lineErrors.set(line.key, 'PRODUCT_CODE_NOT_FOUND');
      continue;
    }

    const dedupeKey = `${productCode.toUpperCase()}|${bucketDate}`;
    const firstKey = firstSeenKeyByDedupe.get(dedupeKey);
    if (firstKey !== undefined) {
      lineErrors.set(line.key, 'DUPLICATE_PRODUCT_DATE');
      lineErrors.set(firstKey, 'DUPLICATE_PRODUCT_DATE');
      continue;
    }
    firstSeenKeyByDedupe.set(dedupeKey, line.key);
  }

  return { headerErrors, lineErrors, filledLineCount };
}

/** แบบนี้ส่งได้หรือยัง — ไม่มี error ของ header/บรรทัดใดเลย และมีอย่างน้อยหนึ่งแถว */
export function isManualPlanDraftValid(validation: ManualPlanValidation): boolean {
  return (
    !validation.headerErrors.plantCode &&
    !validation.headerErrors.horizon &&
    validation.lineErrors.size === 0 &&
    validation.filledLineCount > 0
  );
}

/**
 * ประกอบ body ของ `POST /v1/mps-versions` จากฟอร์ม — คืน `null` เมื่อยังไม่ผ่าน
 * `validateManualPlanDraft` (ด่านเดียวที่ทางเขียนของหน้าจอนี้เดินผ่าน)
 *
 * **ไม่มี `version_label` ในผลลัพธ์เลย** (D-132 ข้อ 1, 2) — หน้าจอนี้ไม่มีช่องให้
 * กรอกชื่อเวอร์ชันอีกแล้ว เซิร์ฟเวอร์เป็นผู้ออกเลขให้เสมอ
 */
export function toMpsVersionCreateBody(
  header: ManualPlanHeaderDraft,
  lines: readonly ManualPlanLineDraft[],
  knownProductCodes: ReadonlySet<string> | null = null,
): MpsVersionCreateBody | null {
  const validation = validateManualPlanDraft(header, lines, knownProductCodes);
  if (!isManualPlanDraftValid(validation)) {
    return null;
  }

  const note = header.note.trim();
  return {
    plant_code: header.plantCode.trim(),
    horizon_start: header.horizonStart.trim(),
    horizon_end: header.horizonEnd.trim(),
    ...(note === '' ? {} : { note }),
    lines: lines
      .filter((line) => !manualPlanLineIsEmpty(line))
      .map((line) => ({
        product_code: line.productCode.trim(),
        bucket_date: line.bucketDate.trim(),
        planned_qty: line.plannedQty.trim(),
      })),
  };
}

/* ---------------------------------- Part Name/Model ในตารางบรรทัด (D-132 ข้อ 2) --- */

/** จำนวนตัวเลือกสูงสุดในดรอปดาวน์ของช่องรหัสสินค้า — ผู้ใช้พิมพ์แคบลงเองถ้ายังไม่เจอ */
const MANUAL_PLAN_PRODUCT_OPTION_LIMIT = 20;

/**
 * ตัวเลือกของช่องรหัสสินค้าบนหน้าสร้างแผน — กรอง**ฝั่งเบราว์เซอร์**จากรายการที่โหลด
 * มาครั้งเดียวตอนเปิดหน้าทั้งชุด (D-132 ข้อ 2, invariant "ทน" — ต่างจาก D-117 ที่ยิง
 * `GET /v1/products?q=` ทุกคำค้น) ด้วยรหัส ชื่อ หรือ Model แบบ substring ไม่สนตัวพิมพ์
 */
export function manualPlanProductOptions(
  products: readonly Product[],
  query: string,
): readonly Product[] {
  if (!isProductSearchQueryEligible(query)) {
    return [];
  }
  const needle = query.trim().toLocaleLowerCase();
  return products
    .filter(
      (product) =>
        product.product_code.toLocaleLowerCase().includes(needle) ||
        product.name.toLocaleLowerCase().includes(needle) ||
        (product.model ?? '').toLocaleLowerCase().includes(needle),
    )
    .slice(0, MANUAL_PLAN_PRODUCT_OPTION_LIMIT);
}

/**
 * ตั้งบรรทัดให้ตรงกับสินค้าที่ resolve แล้ว (D-132 ข้อ 2) — ลอกแนว `withResolvedProduct`
 * ของ `lib/sales/salesView.ts` แต่เขียนแยกที่นี่เพราะตัวนั้นผูกกับ `SalesOrderLineDraft`
 * ใช้ตรง ๆ กับ `ManualPlanLineDraft` ไม่ได้
 */
export function withResolvedManualPlanLineProduct(
  line: ManualPlanLineDraft,
  product: Pick<Product, 'product_code' | 'name' | 'model'>,
): ManualPlanLineDraft {
  return {
    ...line,
    productCode: product.product_code,
    productName: product.name,
    model: product.model ?? '',
  };
}

/** แก้รหัสสินค้าแล้วล้างชื่อ/โมเดลเดิมทันที — เหตุผลเดียวกับ `withClearedProductCode` */
export function withClearedManualPlanLineProductCode(
  line: ManualPlanLineDraft,
  productCode: string,
): ManualPlanLineDraft {
  return { ...line, productCode, productName: '', model: '' };
}

export interface ManualPlanProductSelectOption {
  readonly value: string;
  readonly label: string;
  readonly searchText: string;
}

/**
 * ตัวเลือกของ `SearchableSelect` ในช่องรหัสสินค้าของหน้าสร้างแผน (D-136) — คืน
 * รายการเต็มไม่กรอง/ไม่ตัด เพราะ `SearchableSelect` กรองและจำกัด 200 รายการเองแล้ว
 * ต่างจาก `manualPlanProductOptions` ข้างบนที่ทำสองอย่างนั้นเองสำหรับ autocomplete เดิม
 */
export function manualPlanProductSelectOptions(
  products: readonly Product[],
): readonly ManualPlanProductSelectOption[] {
  return products.map((product) => ({
    value: product.product_code,
    label: productOptionLabel(product),
    searchText: `${product.product_code} ${product.name} ${product.model ?? ''}`,
  }));
}

/* --------------------------- มุมมองรายการ "ตารางแผนทั้งหมด" (D-145, WO-P2-M5-022) --- */

export const PRODUCTION_PLAN_LIST_PAGE_SIZE = 20;

/** แผนใหม่สุดก่อน (D-145 ข้อ 1) — เทียบ `created_at` ตรง ๆ แบบเดียวกับ `pickDefaultMpsVersion` */
export function sortMpsVersionsByCreatedAtDesc(
  items: readonly MpsVersionSummary[],
): readonly MpsVersionSummary[] {
  return [...items].sort((a, b) =>
    a.created_at > b.created_at ? -1 : a.created_at < b.created_at ? 1 : 0,
  );
}

export function productionPlanListPageCount(totalItems: number): number {
  return Math.max(1, Math.ceil(totalItems / PRODUCTION_PLAN_LIST_PAGE_SIZE));
}

export function productionPlanListPageSlice(
  items: readonly MpsVersionSummary[],
  pageIndex: number,
): readonly MpsVersionSummary[] {
  const start = pageIndex * PRODUCTION_PLAN_LIST_PAGE_SIZE;
  return items.slice(start, start + PRODUCTION_PLAN_LIST_PAGE_SIZE);
}

export function productionPlanListRowRangeLabel(totalItems: number, pageIndex: number): string {
  if (totalItems === 0) {
    return '';
  }
  const start = pageIndex * PRODUCTION_PLAN_LIST_PAGE_SIZE + 1;
  const end = Math.min(totalItems, start + PRODUCTION_PLAN_LIST_PAGE_SIZE - 1);
  return `แถว ${start}–${end} จาก ${totalItems}`;
}

/* --------------------------- ตัวกรองของมุมมองรายการ "ตารางแผนทั้งหมด" (D-146/D-161) --- */

export interface ProductionPlanListFilters {
  readonly q: string;
  readonly status: string;
  readonly plant: string;
  readonly releasedFrom: string;
  readonly releasedTo: string;
}

/**
 * กรองฝั่งหน้าจอบน `items` ที่โหลดครบแล้ว — AND ของทุกตัวกรองที่ไม่ว่าง (D-146)
 *
 * **ช่วงวันที่ปล่อยแผนเทียบกับ `released_at` ไม่ใช่ `created_at` อีกแล้ว (D-161)** —
 * เมื่อกำหนดช่วง (`releasedFrom`/`releasedTo` อย่างใดอย่างหนึ่งไม่ว่าง) แผนที่ยังไม่
 * ปล่อย (`released_at === null`) ถูกกรองออกเสมอ ไม่กำหนดช่วงเลย = ไม่กรองอะไร (แสดง
 * ทุกแผนรวมที่ยังไม่ปล่อย)
 */
export function filterMpsVersions(
  items: readonly MpsVersionSummary[],
  filters: Readonly<Partial<ProductionPlanListFilters>>,
): readonly MpsVersionSummary[] {
  const q = (filters.q ?? '').trim().toLocaleLowerCase();
  const status = filters.status ?? '';
  const plant = filters.plant ?? '';
  const releasedFrom = filters.releasedFrom ?? '';
  const releasedTo = filters.releasedTo ?? '';
  const hasReleasedRange = releasedFrom !== '' || releasedTo !== '';

  return items.filter((item) => {
    if (q !== '' && !item.version_label.toLocaleLowerCase().includes(q)) {
      return false;
    }
    if (status !== '' && item.status !== status) {
      return false;
    }
    if (plant !== '' && item.plant_code !== plant) {
      return false;
    }
    if (hasReleasedRange) {
      if (item.released_at === null) {
        return false;
      }
      // เทียบกับวันไทยของ released_at (D-144/D-161) ไม่ใช่สตริง UTC ดิบ — รวมวันต้น-ปลาย
      const releasedDate = planningDateOf(item.released_at);
      if (releasedFrom !== '' && releasedDate < releasedFrom) {
        return false;
      }
      if (releasedTo !== '' && releasedDate > releasedTo) {
        return false;
      }
    }
    return true;
  });
}

/** โรงงานที่ต่างกันของแผนที่โหลดไว้ ตามลำดับที่เจอก่อน — ซ่อน select เมื่อมีค่าเดียว (D-146) */
export function distinctPlantCodes(items: readonly MpsVersionSummary[]): readonly string[] {
  const result: string[] = [];
  for (const item of items) {
    if (!result.includes(item.plant_code)) {
      result.push(item.plant_code);
    }
  }
  return result;
}

/* ------------------------------- หน้ารายละเอียดแผน + ขับสถานะ (WO-P2-M5-011, D-129) --- */

/** เรียงบรรทัดของแผน — ตามวันที่แล้วรหัสสินค้า (ไม่ใช่ `line_no` ที่เซิร์ฟเวอร์ส่งมา) */
export function sortMpsLineDetails(lines: readonly MpsLineDetail[]): readonly MpsLineDetail[] {
  return [...lines].sort((a, b) => {
    const byDate = compareText(a.bucket_date, b.bucket_date);
    return byDate !== 0 ? byDate : compareText(a.product_code, b.product_code);
  });
}

/** จำนวนแถวต่อหน้าของตารางบรรทัด — แบ่งหน้าฝั่งจอเพราะเซิร์ฟเวอร์ส่งทุกบรรทัดมาในคำตอบเดียว */
export const MPS_DETAIL_LINES_PAGE_SIZE = 20;

export function mpsDetailLinesPageCount(totalLines: number): number {
  return Math.max(1, Math.ceil(totalLines / MPS_DETAIL_LINES_PAGE_SIZE));
}

/** `pageIndex` เริ่มที่ 0 — เรียก `sortMpsLineDetails` ก่อนเสมอ ไม่ตัดหน้าของข้อมูลที่ยังไม่เรียง */
export function mpsDetailLinesPageSlice(
  lines: readonly MpsLineDetail[],
  pageIndex: number,
): readonly MpsLineDetail[] {
  const start = pageIndex * MPS_DETAIL_LINES_PAGE_SIZE;
  return lines.slice(start, start + MPS_DETAIL_LINES_PAGE_SIZE);
}

/** `"แถว x–y จาก N"` — `''` เมื่อไม่มีบรรทัดเลย */
export function mpsDetailLinesRowRangeLabel(totalLines: number, pageIndex: number): string {
  if (totalLines === 0) {
    return '';
  }
  const start = pageIndex * MPS_DETAIL_LINES_PAGE_SIZE + 1;
  const end = Math.min(totalLines, start + MPS_DETAIL_LINES_PAGE_SIZE - 1);
  return `แถว ${start}–${end} จาก ${totalLines}`;
}

/** ชื่อภายในของปุ่มขับสถานะ — ผูกกับฟังก์ชัน client ชื่อเดียวกัน (`approveMpsVersion` ฯลฯ) */
export type MpsLifecycleActionKind = 'approve' | 'freeze' | 'release' | 'cancel';

export interface MpsLifecycleButton {
  readonly action: MpsLifecycleActionKind;
  /** action ของ RBAC matrix ที่ปุ่มนี้ต้องถือ — ผ่าน `can()` เท่านั้น ห้าม derive จาก role (D-129 ข้อ 3) */
  readonly requiredPermission: 'APPROVE' | 'EDIT';
  readonly label: string;
}

/**
 * ปุ่มขับสถานะที่สถานะปัจจุบันเปิดให้ — เส้นทางตาม D-096 เป๊ะ `RELEASED`/`CANCELLED`
 * คืน `[]` เสมอ (สถานะสุดท้าย ไม่มีปุ่มใดเลย)
 */
export function mpsLifecycleButtons(status: string): readonly MpsLifecycleButton[] {
  switch (status) {
    case DRAFT_MPS_STATUS:
      return [
        { action: 'approve', requiredPermission: 'APPROVE', label: 'อนุมัติ' },
        { action: 'cancel', requiredPermission: 'EDIT', label: 'ยกเลิกแผน' },
      ];
    case APPROVED_MPS_STATUS:
      return [
        { action: 'freeze', requiredPermission: 'APPROVE', label: 'ตรึงแผน' },
        { action: 'release', requiredPermission: 'APPROVE', label: 'ปล่อยแผน' },
        { action: 'cancel', requiredPermission: 'EDIT', label: 'ยกเลิกแผน' },
      ];
    case FROZEN_MPS_STATUS:
      return [
        { action: 'release', requiredPermission: 'APPROVE', label: 'ปล่อยแผน' },
        { action: 'cancel', requiredPermission: 'EDIT', label: 'ยกเลิกแผน' },
      ];
    default:
      return [];
  }
}

/**
 * `frozen_until` ที่กรอกต้องไม่ว่างและอยู่ในช่วง `[horizon_start, horizon_end]` (D-096)
 * — เทียบสตริง ISO ตรง ๆ ได้โดยไม่ต้องผ่าน `Date` เพราะรูปแบบเรียงตามเวลาอยู่แล้ว
 */
export function isFrozenUntilWithinHorizon(
  frozenUntil: string,
  horizonStart: string,
  horizonEnd: string,
): boolean {
  const value = frozenUntil.trim();
  return value !== '' && value >= horizonStart && value <= horizonEnd;
}

/**
 * ฟอร์มสร้างแผนใหม่ที่เติมล่วงหน้าจากแผนต้นทาง (D-129 ข้อ 2) — ไม่มีชื่อเวอร์ชันให้
 * เว้นอีกแล้ว (D-132 ข้อ 1) ส่วนที่เหลือคัดลอกมาตรง ๆ รวมทุกบรรทัด เรียงแบบเดียวกับ
 * ตารางหน้ารายละเอียดเพื่อให้ลำดับที่เห็นตรงกับลำดับที่กำลังจะคัดลอก
 *
 * **`productName`/`model` เริ่มว่างเสมอ** — หน้าจอ resolve ให้เองอีกครั้งจากรหัสที่
 * คัดลอกมา ทันทีที่ทะเบียนสินค้าโหลดเสร็จ (ดู effect ใน `ManualPlanCreateView.tsx`)
 */
export function manualPlanDraftFromVersion(
  version: MpsVersionDetail,
): { readonly header: ManualPlanHeaderDraft; readonly lines: readonly ManualPlanLineDraft[] } {
  const header: ManualPlanHeaderDraft = {
    plantCode: version.plant_code,
    horizonStart: version.horizon_start,
    horizonEnd: version.horizon_end,
    note: version.note ?? '',
  };
  const lines = sortMpsLineDetails(version.lines).map((line, index) => ({
    key: index,
    productCode: line.product_code,
    productName: '',
    model: '',
    bucketDate: line.bucket_date,
    plannedQty: line.planned_qty,
  }));
  return { header, lines };
}
